/**
 * Sales Dashboard - Real API Integration
 * Displays sales metrics, analytics, and real-time business data
 * Fetches data from Commerce Service APIs (Port 8083)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, RefreshCw, TrendingUp, Users, Package, DollarSign, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { salesService } from '../services/salesService';
import DateRangeFilter from '../components/DateRangeFilter';
import { RevenueChart } from '../components/charts/RevenueChart';
import { AvailableBatchesChart } from '../components/charts/AvailableBatchesChart';
import { CustomerGrowthChart } from '../components/charts/CustomerGrowthChart';
import { MarketplaceMetricsCard } from '../components/charts/MarketplaceMetricsCard';
import { RecentOrdersPanel } from '../components/charts/RecentOrdersPanel';
import { OrderStatusChart } from '../components/charts/OrderStatusChart';
import { InventoryAlertsPanel } from '../components/charts/InventoryAlertsPanel';
import GaushalaMapChart from '../components/charts/GaushalaMapChart';
import { DateRange, SalesDashboardSummary } from '../types';

const CACHE_KEY = 'sales_dashboard_cache';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function readDashboardCache(): SalesDashboardSummary | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data as SalesDashboardSummary;
  } catch {
    return null;
  }
}

function writeDashboardCache(data: SalesDashboardSummary) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

/**
 * Metric Card Component
 */
function MetricCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-gray-600" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Skeleton — shown while data loads. Matches dashboard layout exactly so there is
 * no layout shift or full-viewport flash when real data arrives.
 */
function DashboardSkeleton() {
  const pulse = 'bg-gray-200 animate-pulse rounded';
  return (
    <div className="min-h-0 bg-gray-50">
      {/* Header skeleton */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className={`${pulse} h-8 w-48 mb-2`} />
            <div className={`${pulse} h-4 w-64`} />
          </div>
          <div className="flex gap-3">
            <div className={`${pulse} h-9 w-32`} />
            <div className={`${pulse} h-9 w-24`} />
          </div>
        </div>
      </div>
      {/* Metric cards skeleton */}
      <div className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border p-6 space-y-3">
              <div className={`${pulse} h-4 w-24`} />
              <div className={`${pulse} h-8 w-32`} />
              <div className={`${pulse} h-3 w-20`} />
            </div>
          ))}
        </div>
        {/* Chart rows skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`${pulse} h-64 w-full`} />
          <div className={`${pulse} h-64 w-full`} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className={`${pulse} h-48 w-full`} />
          <div className={`${pulse} h-48 w-full`} />
        </div>
      </div>
    </div>
  );
}

/**
 * Error Message
 */
function ErrorMessage({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center p-4 min-h-[400px]">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full space-y-4">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-500" />
          <h2 className="text-xl font-semibold text-gray-900">Error Loading Dashboard</h2>
        </div>
        <p className="text-gray-600">{message}</p>
        <button
          onClick={onRetry}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

/**
 * Sales Dashboard Component
 */
// In-memory cache survives StrictMode remounts; localStorage cache survives page refresh
const memCache = { current: null as SalesDashboardSummary | null };

export default function SalesDashboard() {
  // Read once — used for BOTH dashboardData and loading initial values
  const initialData = memCache.current ?? readDashboardCache();

  const [dashboardData, setDashboardData] = useState<SalesDashboardSummary | null>(
    () => initialData
  );
  const [loading, setLoading] = useState(() => initialData === null);
  const [error, setError] = useState<string | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const hasFetched = React.useRef(false);

  useEffect(() => {
    if (hasFetched.current) return; // StrictMode guard
    hasFetched.current = true;
    // Always fetch fresh data in background; if cache was shown, it's a silent refresh
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (dateRange?: DateRange, isManualRefresh = false) => {
    try {
      const hasCache = memCache.current !== null || readDashboardCache() !== null;
      if (!hasCache) setLoading(true);
      if (isManualRefresh) setRefreshing(true);
      setError(null);

      const response = await salesService.getDashboardSummary(
        dateRange?.startDate,
        dateRange?.endDate
      );

      if (response.success && response.data) {
        memCache.current = response.data;
        writeDashboardCache(response.data);
        setDashboardData(response.data);
        setLastUpdated(new Date());
        if (isManualRefresh) {
          toast.success('Dashboard refreshed successfully!', { duration: 3000 });
        }
      } else {
        throw new Error('Failed to fetch dashboard data');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard. Please check your connection.';
      // Only show error if we have no data at all to display
      if (!dashboardData) setError(errorMessage);
      if (isManualRefresh) {
        toast.error('Failed to refresh dashboard. Please try again.', { duration: 4000 });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /**
   * Handle date range change
   */
  const handleDateRangeChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
    fetchDashboardData(dateRange);
  };

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorMessage message={error} onRetry={fetchDashboardData} />;
  if (!dashboardData) return <ErrorMessage message="No dashboard data available" onRetry={fetchDashboardData} />;

  const { metrics, revenueData, orderStatusData, customerGrowthData, marketplaceMetrics, recentOrders, inventoryAlerts, inventoryBatches, dateRange } = dashboardData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sales Dashboard</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Real-time sales performance and analytics
              </p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Date Range Filter */}
              <DateRangeFilter
                onDateRangeChange={handleDateRangeChange}
                selectedLabel={selectedDateRange?.label || 'Last 7 Days'}
              />

              {/* Refresh Button */}
              <button
                onClick={() => {
                  if (!refreshing) {
                    fetchDashboardData(selectedDateRange || undefined, true);
                  }
                }}
                disabled={refreshing}
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${
                  refreshing
                    ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                }`}
                title="Refresh dashboard data"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </button>

              {/* Last Updated */}
              {lastUpdated && (
                <div className="text-right hidden sm:block">
                  <div className="text-xs text-muted-foreground">Updated</div>
                  <div className="text-sm font-semibold text-gray-900">
                    {lastUpdated.toLocaleTimeString('en-IN')}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Date Range Info */}
          <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
            Showing data from {dateRange.startDate} to {dateRange.endDate}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Metric Cards Section */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Revenue"
              value={`₹${(metrics.totalRevenue / 1000).toFixed(1)}K`}
              icon={DollarSign}
              description="Period revenue"
            />
            <MetricCard
              title="Total Orders"
              value={metrics.totalOrders}
              icon={Package}
              description="Orders processed"
            />
            <MetricCard
              title="Total Customers"
              value={metrics.totalCustomers}
              icon={Users}
              description="Active customers"
            />
            <MetricCard
              title="Avg Order Value"
              value={`₹${metrics.averageOrderValue.toFixed(2)}`}
              icon={TrendingUp}
              description="Average per order"
            />
          </div>
        </section>

        {/* Analytics Section */}
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Analytics</h2>

          {/* Row 1: Gaushala Locations Map + Revenue Trend (2 Columns) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Gaushala Locations Map - relative z-0 to contain map z-index and prevent overlap with sidebar */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden relative z-0 isolate">
              <GaushalaMapChart height="400px" showStatistics={false} />
            </div>

            {/* Revenue Chart */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  Revenue Trend
                </CardTitle>
                <CardDescription className="text-xs">
                  Daily revenue over selected period
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RevenueChart data={revenueData} />
              </CardContent>
            </Card>
          </div>

          {/* Row 2: Customer Growth + Marketplace Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Customer Growth Chart */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="w-4 h-4 text-purple-600" />
                  Customer Growth
                </CardTitle>
                <CardDescription className="text-xs">
                  Cumulative customer registration
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CustomerGrowthChart
                  data={customerGrowthData}
                />
              </CardContent>
            </Card>

            {/* Marketplace Metrics */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  📦 Marketplace Metrics
                </CardTitle>
                <CardDescription className="text-xs">
                  Product listings and inquiries
                </CardDescription>
              </CardHeader>
              <CardContent>
                <MarketplaceMetricsCard metrics={marketplaceMetrics} />
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Order Status Distribution + Recent Orders */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Order Status Distribution */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-4 h-4 text-indigo-600" />
                  Order Status Distribution
                </CardTitle>
                <CardDescription className="text-xs">
                  Orders grouped by confirmation status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <OrderStatusChart data={orderStatusData} />
              </CardContent>
            </Card>

            {/* Recent Orders */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Package className="w-4 h-4 text-orange-600" />
                  Recent Orders
                </CardTitle>
                <CardDescription className="text-xs">
                  Latest 5 orders
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RecentOrdersPanel orders={recentOrders} />
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer Info */}
        <section className="text-center text-xs text-gray-500 py-4 border-t">
          <p>Data is automatically refreshed from the Commerce Service API</p>
          <p>Last refreshed: {lastUpdated?.toLocaleString('en-IN')}</p>
        </section>
      </div>
    </div>
  );
}
